import { useApiClient } from '../../../hooks/useApiClient';
import {
  PasskeyListItem,
  PasskeyRemoval,
  listPasskeys,
  removePasskey,
  editPasskey,
  PasskeyEdit,
} from '../../../utils/apiWrappers/PasskeysApiWrapper';
import PageTitleHook from '../../../utils/PageTitleHook/PageTitleHook';
import React, { useContext, useEffect, useState, useRef, ReactComponentElement } from 'react';
import { Button, SectionalAlert, MenuDropdown, ValidatedInput, Modal } from '../../baseComponents';
import { pathContext, configurationContext, myAccountContext } from '../../../contexts/contexts';
import './passkeys.css';
import LastUsed from './LastUsed';
import { MenuPencilIcon } from '../../../assets/icons/MenuPencilIcon';
import { MenuXIcon } from '../../../assets/icons/MenuXIcon';
import { setPasskeyFlagToLocalMemory } from '../../../utils/utils';

interface ModalProps {
  title: string;
  visible: boolean;
  children: ReactComponentElement<any>;
  hideCancelButton: boolean;
  onCancel: () => void;
  onOk: () => void;
}

const emptyModal = {
  title: '',
  visible: false,
  children: <></>,
  hideCancelButton: true,
  onCancel: () => null,
  onOk: () => null,
};

export const Passkeys = () => {
  const { configuration } = useContext(configurationContext);
  const { appSettings } = configuration;
  const { setUrl } = useContext(pathContext);
  const apiClient = useApiClient();
  const [passkeysList, setPasskeysList] = useState<Array<PasskeyListItem>>([]);
  const { alert, setAlert, user, setUser, clearPersistentAlert } = useContext(myAccountContext);
  const sectionalAlertRef = useRef<HTMLDivElement>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalProps, setModalProps] = useState<ModalProps>(emptyModal);
  const [passkeyInputVal, setPasskeyInputVal] = useState('');
  const passkeyInputRef = useRef<HTMLInputElement>(null);
  const [passkeyValidationMsg, setPasskeyValidationMsg] = useState('');
  const [selectedPasskey, setSelectedPasskey] = useState<PasskeyListItem>({
    id: -1,
    friendly_name: '',
    last_used_at: '',
  });
  const [selectedValue, setSelectedValue] = useState('');

  PageTitleHook('USCIS Online Account | Manage Passkeys');

  useEffect(() => {
    if (!appSettings.includes('PasskeysEnabled')) {
      setUrl('/edit-account');
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function fetchPasskeys() {
      setPasskeysList(await listPasskeys(apiClient));
    }

    fetchPasskeys();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (alert.message !== '' && sectionalAlertRef.current) {
      sectionalAlertRef.current!.focus();
    }
  }, [alert]);

  useEffect(() => {
    switch (selectedValue) {
      case 'Edit':
        handleEditModal();
        break;
      case 'Remove':
        handleRemoveConfirmationModal();
        break;
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPasskey, selectedValue]);

  useEffect(() => {}, [showModal]);

  useEffect(() => {
    setModalProps({
      title: 'Change Passkey name',
      visible: showModal,
      children: (
        <div data-testid="passkey-input" className="my-3 pt-3">
          <ValidatedInput
            label="New passkey name"
            required
            msg={passkeyValidationMsg}
            onChange={handlePasskeyInputChange}
            value={passkeyInputVal}
            type="text"
            inputMode="text"
            ref={passkeyInputRef}
            onKeyDown={handleEnterDown}
          />
        </div>
      ),
      hideCancelButton: false,
      onCancel: handleOnModalCancel,
      onOk: handleOnEdit,
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passkeyInputVal, passkeyValidationMsg]);

  const handleCancelButton = (e: any) => {
    clearPersistentAlert();
    if (user.passkeys_helper === 'passkey_added_from_nudge') {
      apiClient.post('users/set_passkeys_helper', { passkey_helper: 'reset_passkey_helper' }).then((res) => {
        setUser(res.data);
        setUrl('/dashboard');
      });
    } else {
      setUrl('/edit-account');
    }
  };

  const handleNewPasskeyButton = (e: any) => {
    setUrl('/passkeys/register');
  };

  const handleOnModalCancel = () => {
    if (selectedValue === 'Edit') {
      setPasskeyInputVal(selectedPasskey.friendly_name);
    }
    setShowModal(false);
  };

  const passkeyDropdownOptions = [
    {
      name: 'Edit',
      icon: MenuPencilIcon,
    },
    {
      name: 'Remove',
      icon: MenuXIcon,
    },
  ];

  const handlePasskeyDropdownSelect = async (value: string, passkey: PasskeyListItem) => {
    setSelectedPasskey(passkey);
    setSelectedValue(value);
    setShowModal(true);
  };

  const handleEnterDown = (e: any) => {
    if (e.key === 'Enter') {
      if (passkeyValidationMsg !== '') {
        passkeyInputRef.current!.focus();
      } else {
        handleOnEdit();
      }
    }
  };

  const handleOnEdit = async () => {
    setPasskeyValidationMsg('');

    if (passkeyInputVal !== '') {
      const editStatus: PasskeyEdit = await editPasskey(apiClient, selectedPasskey, passkeyInputVal);

      if (editStatus.success && editStatus.user) {
        setAlert({ type: 'success', message: 'Your passkey has been successfully renamed.' });

        setUser(editStatus.user);
        setPasskeysList(editStatus.user.passkeys);
      } else {
        setAlert({ type: 'error', message: 'There was a problem editing your passkey.' });
      }
      handleOnModalCancel();
    } else {
      setPasskeyValidationMsg('Passkey name cannot be blank');
    }
  };

  const handlePasskeyInputChange = (e: any) => {
    setPasskeyInputVal(e.target.value);
  };

  const handleEditModal = async () => {
    setPasskeyInputVal(selectedPasskey.friendly_name);
    setModalProps({
      title: 'Change Passkey name',
      visible: showModal,
      children: (
        <div data-testid="passkey-input" className="my-3 pt-3">
          <ValidatedInput
            label="New passkey name"
            required
            msg={passkeyValidationMsg}
            onChange={handlePasskeyInputChange}
            value={passkeyInputVal}
            type="text"
            inputMode="text"
            ref={passkeyInputRef}
            onKeyDown={handleEnterDown}
          />
        </div>
      ),
      hideCancelButton: false,
      onCancel: handleOnModalCancel,
      onOk: handleOnEdit,
    });
    setShowModal(true);
  };

  const handleRemoveConfirmationModal = async () => {
    setModalProps({
      title: 'Remove Passkey?',
      visible: showModal,
      children: (
        <p>
          Are you sure you want to remove <b>{selectedPasskey.friendly_name}?</b>
        </p>
      ),
      hideCancelButton: false,
      onCancel: handleOnModalCancel,
      onOk: handleRemoveButton,
    });
    setShowModal(true);
  };

  const handleRemoveButton = async () => {
    const removal: PasskeyRemoval = await removePasskey(apiClient, selectedPasskey);

    if (removal.success && removal.user) {
      setAlert({ type: 'success', message: 'Your passkey has been successfully removed.' });

      setUser(removal.user);
      setPasskeysList(removal.user.passkeys);

      if (removal.user.passkeys.length === 0) {
        setPasskeyFlagToLocalMemory('false');
      }
    } else {
      setAlert({ type: 'error', message: 'There was a problem removing your passkey.' });
    }
    handleOnModalCancel();
  };

  return (
    <div className="card" data-testid="manage-passkeys-container">
      <Modal
        title={modalProps.title}
        visible={showModal}
        hideCancelButton={modalProps.hideCancelButton}
        onCancel={modalProps.onCancel}
        onOk={modalProps.onOk}
      >
        {modalProps.children}
      </Modal>

      <h1 className="card-header text-left" data-testid="page-title">
        Manage Passkeys
      </h1>

      {alert.message !== '' && (
        <div tabIndex={-1} id="sectional-alert" data-testid="sectional-alert" ref={sectionalAlertRef} className="mb-6">
          <SectionalAlert type={alert.type}>{alert.message}</SectionalAlert>
        </div>
      )}

      <div className="card-body p-0 space-y-6 text-md my-4 mb-8">
        {passkeysList.map((passkey) => (
          <div key={passkey.id} className="grid grid-cols-6 passkey-list-row">
            <div className="col-span-2 pr-1">
              <strong>{passkey.friendly_name}</strong>
            </div>
            <div className="col-span-3">
              <LastUsed timestamp={passkey.last_used_at} />
            </div>
            <div className="text-right col-span-1">
              <MenuDropdown
                dataTestId={'passkey-' + passkey.id}
                label={'Passkey options for: ' + passkey.friendly_name}
                options={passkeyDropdownOptions}
                data={passkey}
                onSelect={handlePasskeyDropdownSelect}
              />
            </div>
          </div>
        ))}
        <div className="">
          <div className="mb-6">
            <Button
              id="new-passkey-btn"
              data-testid="new-passkey-btn"
              text="New Passkey"
              onClick={handleNewPasskeyButton}
              type="primary"
              className="mr-5"
            />
            <Button
              id="cancel-btn"
              data-testid="cancel-btn"
              text={user.passkeys_helper === 'passkey_added_from_nudge' ? 'Close' : 'Cancel'}
              type="cancel"
              onClick={handleCancelButton}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Passkeys;
